import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, Palmtree, Plus } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, PageHeader, ReasonsList, RestrictedState, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { VAC6_STATUS, VAC6_CONFLICT, fmtDateTime } from "../lib/labels";

const TABS = [["cuadro", "Cuadro de mando"], ["solicitudes", "Solicitudes"], ["calendario", "Calendario"], ["delegaciones", "Delegaciones"]];
const BY_STATUS = {
  borrador: ["solicitar"], pendiente_confirmacion: ["confirmar_empleado"],
  solicitada: ["solicitar"], cambios_solicitados: ["solicitar"],
  pendiente_aprobacion: ["aprobar", "rechazar", "solicitar_cambios"],
  pendiente_informacion: ["responder_informacion"],
  aprobada: ["solicitar_modificacion", "solicitar_cancelacion"],
  registrada_calendario: ["solicitar_modificacion", "solicitar_cancelacion"],
  modificacion_solicitada: ["aprobar_modificacion", "rechazar_modificacion"],
  cancelacion_solicitada: ["aprobar_cancelacion", "rechazar_modificacion"],
};
const ACTION_LABELS = {
  solicitar: "Solicitar aprobación", confirmar_empleado: "Confirmar solicitud (empleado)",
  aprobar: "Aprobar", rechazar: "Rechazar", solicitar_cambios: "Solicitar cambios",
  responder_informacion: "Responder información", solicitar_modificacion: "Solicitar modificación",
  aprobar_modificacion: "Aprobar modificación", solicitar_cancelacion: "Solicitar cancelación",
  aprobar_cancelacion: "Aprobar cancelación", rechazar_modificacion: "Rechazar modificación/cancelación",
};

export default function Vacaciones() {
  const { user } = useAuth();
  const { map, users } = useUsers();
  const qc = useQueryClient();
  const [tab, setTab] = useState("cuadro");
  const [dlg, setDlg] = useState(null);
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);

  const refresh = () => qc.invalidateQueries();
  const { data: cuadro, error: cuadroErr } = useQuery({ queryKey: ["vac-cuadro"], queryFn: async () => (await api.get("/vacaciones/cuadro")).data });
  const { data: reqs } = useQuery({ queryKey: ["vac-list"], queryFn: async () => (await api.get("/vacaciones")).data });
  const { data: cal } = useQuery({ queryKey: ["vac-cal"], queryFn: async () => (await api.get("/vacaciones/calendario")).data });
  const { data: dels } = useQuery({ queryKey: ["vac-del"], queryFn: async () => (await api.get("/vacaciones/delegaciones")).data });

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDlg(null); setF({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error"); },
  });
  const open = (d) => { setDlg(d); setF({}); setErr(null); };
  const act = (rid, action, extra = {}) => mut.mutate({ path: `/vacaciones/${rid}/accion`, body: { action, ...extra } });

  if (user?.is_contractor || cuadroErr?.response?.status === 403) {
    return (
      <div className="space-y-4" data-testid="vacaciones-page">
        <PageHeader title="Vacaciones y ausencias" subtitle="" testid="vacaciones-title" />
        <RestrictedState module="las vacaciones y ausencias del equipo" detail={cuadroErr?.response?.data?.detail} testid="vacaciones-restricted" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vacaciones-page">
      <PageHeader title="Vacaciones y ausencias"
        subtitle="Cálculo de días determinista, conflictos con regla explícita, aprobación con maker-checker, calendario idempotente y delegaciones sin elevación de permisos."
        testid="vacaciones-title"
        actions={<Button onClick={() => open({ kind: "nueva" })} data-testid="vac-new-btn"><Plus className="mr-1 h-4 w-4" /> Nueva solicitud</Button>} />
      <div className="flex flex-wrap gap-1">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === k ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
        ))}
      </div>

      {tab === "cuadro" && cuadro && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7" data-testid="vac-cuadro">
          {Object.entries(cuadro).map(([k, v]) => (
            <button key={k} className="ts-card ts-card-hover p-3 text-left" data-testid={`kpi-${k}`} onClick={() => setTab("solicitudes")}>
              <p className="ts-caption">{k.replaceAll("_", " ")}</p>
              <p className={`font-display text-2xl font-bold ${v ? "text-slate-900" : "text-slate-500"}`}>{v}</p>
            </button>
          ))}
        </div>
      )}

      {tab === "solicitudes" && (
        <div className="space-y-2" data-testid="vac-list">
          {(reqs || []).map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`vac-${r.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold">{r.employee_name}</span>
                <Chip map={VAC6_STATUS} value={r.status} testid={`vac-status-${r.id}`} />
                <ToneChip label={r.absence_type_label} tone="info" />
                {r.origin === "email" && <ToneChip label="desde email" tone="ai" />}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {String(r.start_date).slice(0, 10)} → {String(r.end_date).slice(0, 10)} · {r.days?.dias_laborables} días laborables ({r.days?.calendario})
                {r.days?.festivos?.length ? ` · festivos: ${r.days.festivos.join(", ")}` : ""}
                {r.backup_id ? ` · backup ${userName(map, r.backup_id)}` : " · sin backup"}
                {" · manager "}{userName(map, r.manager_id)}
              </p>
              {(r.conflicts || []).length > 0 && (
                <div className="mt-1 space-y-0.5" data-testid={`vac-conflicts-${r.id}`}>
                  {r.conflicts.map((c, i) => (
                    <p key={i} className={`text-xs ${c.level === "bloqueo" ? "text-red-700 font-medium" : c.level === "advertencia" ? "text-amber-700" : "text-slate-500"}`}>
                      [{VAC6_CONFLICT[c.level]?.label || c.level}] {c.detail} {c.resolution ? `→ ${c.resolution}` : ""}
                    </p>
                  ))}
                </div>
              )}
              {r.rejection && <p className="mt-1 text-xs text-red-600">Rechazada: {r.rejection.motivo}</p>}
              {r.modification && r.status === "modificacion_solicitada" && (
                <p className="mt-1 text-xs text-amber-700">Modificación: {r.modification.original.start_date}→{r.modification.original.end_date} pasa a {r.modification.nueva_fecha_inicio}→{r.modification.nueva_fecha_fin} ({r.modification.motivo})</p>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                {(BY_STATUS[r.status] || []).map((a) => (
                  <Button key={a} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`vac-${a}-${r.id}`}
                    onClick={() => ["rechazar", "solicitar_cambios", "solicitar_modificacion", "solicitar_cancelacion", "rechazar_modificacion", "responder_informacion"].includes(a)
                      ? open({ kind: "accion", req: r, action: a }) : act(r.id, a)}>
                    {ACTION_LABELS[a]}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "calendario" && (
        <div className="space-y-2" data-testid="vac-cal">
          <p className="text-xs text-slate-500"><CalendarDays className="mr-1 inline h-3.5 w-3.5" /> Calendario mock — un evento por solicitud aprobada; reintentos no duplican.</p>
          {(cal || []).map((e) => (
            <div key={e.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid={`cal-${e.id}`}>
              <span className="font-medium">{e.title}</span>
              <span className="ml-2 text-slate-500">{String(e.start).slice(0, 10)} → {String(e.end).slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "delegaciones" && (
        <div className="space-y-2" data-testid="vac-dels">
          <p className="text-xs text-slate-500">Las delegaciones nunca elevan permisos ni incluyen firma, aprobación financiera, aceptación de riesgos, revisión legal ni privilegios.</p>
          {(dels || []).map((d) => (
            <div key={d.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid={`del-${d.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium">{userName(map, d.delegator_id)} → {userName(map, d.delegate_id)}</span>
                <ToneChip label={d.status} tone={d.status === "activa" ? "success" : "muted"} />
              </div>
              <p className="mt-1 text-slate-500">{String(d.period_start).slice(0, 10)} → {String(d.period_end).slice(0, 10)} · ámbito: {(d.scope || []).join(", ")} · excluido: {(d.excluded || []).join(", ")}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent data-testid="vac-dialog">
          <DialogHeader><DialogTitle>
            {dlg?.kind === "nueva" && "Nueva solicitud de ausencia"}
            {dlg?.kind === "accion" && `${ACTION_LABELS[dlg.action]} — ${dlg.req.employee_name}`}
          </DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {dlg.kind === "nueva" && (
                <>
                  <div><Label>Empleado *</Label>
                    <Select value={f.employee_id || ""} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                      <SelectTrigger data-testid="f-employee"><SelectValue placeholder="Empleado" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Inicio *</Label><Input type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="f-start" /></div>
                    <div><Label>Fin *</Label><Input type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="f-end" /></div>
                  </div>
                  <div><Label>Tipo</Label>
                    <Select value={f.absence_type || "vacaciones"} onValueChange={(v) => setF({ ...f, absence_type: v })}>
                      <SelectTrigger data-testid="f-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vacaciones">Vacaciones</SelectItem><SelectItem value="asuntos_propios">Asuntos propios</SelectItem>
                        <SelectItem value="permiso">Permiso retribuido</SelectItem><SelectItem value="teletrabajo_puntual">Teletrabajo puntual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Backup (opcional)</Label>
                    <Select value={f.backup_id || ""} onValueChange={(v) => setF({ ...f, backup_id: v || undefined })}>
                      <SelectTrigger data-testid="f-backup"><SelectValue placeholder="Sin backup" /></SelectTrigger>
                      <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {dlg.kind === "accion" && ["rechazar", "solicitar_cambios", "solicitar_cancelacion", "rechazar_modificacion"].includes(dlg.action) && (
                <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
              )}
              {dlg.kind === "accion" && dlg.action === "responder_informacion" && (
                <div><Label>Respuesta *</Label><Input value={f.respuesta || ""} onChange={(e) => setF({ ...f, respuesta: e.target.value })} data-testid="f-respuesta" /></div>
              )}
              {dlg.kind === "accion" && dlg.action === "solicitar_modificacion" && (
                <>
                  <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Nuevo inicio *</Label><Input type="date" value={f.nueva_fecha_inicio || ""} onChange={(e) => setF({ ...f, nueva_fecha_inicio: e.target.value })} data-testid="f-nstart" /></div>
                    <div><Label>Nuevo fin *</Label><Input type="date" value={f.nueva_fecha_fin || ""} onChange={(e) => setF({ ...f, nueva_fecha_fin: e.target.value })} data-testid="f-nend" /></div>
                  </div>
                </>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="vac-error">{apiError(err)}</p>)}
              <Button data-testid="vac-dialog-submit" disabled={mut.isPending}
                onClick={() => {
                  if (dlg.kind === "nueva") mut.mutate({ path: "/vacaciones", body: f });
                  else act(dlg.req.id, dlg.action, f);
                }}>Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
